import type {
  OrderGetResponseType,
  OrderPaymentType,
} from "@sellar-io/api-v2/types";
import { formatCurrency } from "@sellar-io/common";
import dayjs from "dayjs";
import { MouseEvent, ReactElement, useState } from "react";
import { useNavigate } from "react-router";
import { COLLECTIONS, PAYMENT_LINK_REF } from "../../constants/routes";
import { useOrderIntegrationValueList } from "../../hooks/api/v2/order/useOrderIntegrationValueList";
import { useOrderInvoiceUrl } from "../../hooks/api/v2/order/useOrderInvoiceUrl";
import { useOrderCommentCreate } from "../../hooks/api/v2/orderComment/useOrderCommentCreate";
import { usePaymentList } from "../../hooks/api/v2/payment/usePaymentList";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { useShowTermsAndConditionsModal } from "../../hooks/useShowTermsAndConditionsModal";
import { ConfirmOrderDialog } from "../../pages/order/modals/ConfirmOrderDialog";
import { OrderPaidModal } from "../../pages/order/modals/OrderPaidModal";
import { OrderRequestCardPaymentModal } from "../../pages/order/modals/OrderRequestCardPaymentModal";
import { SendPaymentLinkModal } from "../../pages/order/modals/SendPaymentLinkModal";
import { OrderSendInvoiceModal } from "../../pages/order/modals/OrderSendInvoiceModal";
import { OrderSendInvoiceReminderModal } from "../../pages/order/modals/OrderSendReminderModal";
import { OrderShippingLabelModal } from "../../pages/order/modals/OrderShippingLabelModal";
import { TermsAndConditionsDialog } from "../../pages/settings/termsAndConditions/TermsAndConditionsDialog";
import { PaymentFailedDialog } from "../../pages/stockList/checkoutV2/lib/PaymentFailedDialog";
import { ActionPanel } from "../actionPanel/ActionPanel";
import { BuyerOnly } from "../authFilter/BuyerOnly";
import { SupplierOnly } from "../authFilter/SupplierOnly";
import { CommentDialog } from "../comments/CommentDialog";
import { Button, H5, Icon } from "@sellar-io/components";
import { TooltipContainer } from "@sellar-io/components";
import { OrderVatInvoiceActionPanel } from "./OrderVatInvoiceActionPanel";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../modal/Dialog";
import { Alert } from "../alert/Alert";
import { InputLabel } from "@sellar-io/components";
import { RadioSelector, RadioSelectorOption } from "@sellar-io/components";
import { useOrderUpdate } from "../../hooks/api/v2/order/v2/useOrderUpdate";
import { apiV2AssertError } from "../../hooks/api/v2/apiV2Request";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { PayWithSellarCreditActionPanelLegacy } from "./PayWithSellarCreditActionPanelLegacy";
import { SellarCreditOrderVatInvoiceActionPanel } from "./SellarCreditOrderVatInvoiceActionPanel";
import { PayWithSellarCreditActionPanel } from "./PayWithSellarCreditActionPanel";
import { useCreditProcessorGetDeferredPayment } from "../../hooks/api/v2/creditProcessor/useCreditProcessorGetDeferredPayment";
import { SellarCreditOrderNotPaid } from "./SellarCreditOrderNotPaid";
import { useSellarUser } from "../../hooks";
import { useBuildStripeDashboardUrl } from "../../utils/stripeDashboardUrl";
import { CardPaymentWithDepositFailedDialog } from "../../pages/stockList/checkoutV2/lib/CardPaymentWidthDepositFailedDialog";
import { CardChargeRemainingBalancePaymentFailedDialog } from "../../pages/stockList/checkoutV2/lib/CardChargeRemainingBalancePaymentFailedDialog";
import { useSellarCreditPaymentStatus } from "./useSellarCreditPaymentStatus";
import { useLegalEntityCompanyCreditInfo } from "../../hooks/api/v2/legalEntity/useLegalEntityCompanyCreditInfo";
import { SellarDistributionPalletActionPanel } from "./SellarDistributionPalletActionPanel";
import { useOrderDistributionDeliveryArea } from "../../hooks/api/v2/deliveryArea/useOrderDistributionDeliveryArea";
import { Switch } from "@sellar-io/components";

export const OrderActionPanel = ({
  confirmedAt,
  paymentStatus,
  paymentDue,
  invoiceFinalizedAt,
  paymentType,
  paymentTerms,
  supplierName,
  buyerName,
  buyerPostcode,
  orderId,
  supplierId,
  stripeInvoiceId,
  stripeAccountId,
  setMessageType,
  fulfillmentType,
  deliveredAt,
  dispatchedAt,
  voidedAt,
  invoiceCreationStage,
  invoiceIssuedAt,
  paidAt,
  paidAmount,
  price,
  hasNoDraughtDutyRelief,
  acceptedAt,
  deferredPaymentId,
  initiator,
  dispatchPlannedAt,
  twoOrder,
  SupplierPalletShipment,
  dueDays,
  hasBankDetails,
  invoicesEnabled,
  retailerInvoiceEmails,
  hasPaymentLink,
  paymentLinkCode,
  isImplicitProformaEnabled = false,
}: {
  orderId: number;
  supplierId: number;
  confirmedAt: Date | null;
  acceptedAt: Date | null;
  dispatchedAt: Date | null;
  voidedAt: Date | null;
  dispatchPlannedAt: Date | null;
  deliveredAt: Date | null;
  paymentDue: Date | null;
  invoiceFinalizedAt: Date | null;
  invoiceIssuedAt: Date | null;
  paidAt: Date | null;
  supplierName: string;
  buyerName: string;
  buyerPostcode: string;
  fulfillmentType?: OrderGetResponseType["fulfillmentType"];
  paymentType: OrderPaymentType | null;
  paymentTerms?: "credit" | "proforma" | "paymentOnDelivery" | null;
  paymentStatus?: OrderGetResponseType["paymentStatus"];
  invoiceCreationStage: OrderGetResponseType["Supplier"]["invoiceCreationStage"];
  stripeInvoiceId?: string | null;
  stripeAccountId?: string | null;
  setMessageType?: (type: string) => void;
  paidAmount: number;
  price: number;
  hasNoDraughtDutyRelief: boolean;
  deferredPaymentId: string | null;
  initiator: "supplier" | "retailer" | null;
  twoOrder: OrderGetResponseType["TwoOrder"];
  SupplierPalletShipment: OrderGetResponseType["SupplierPalletShipment"];
  dueDays: number | null;
  hasBankDetails: boolean;
  invoicesEnabled: boolean;
  retailerInvoiceEmails?: string[] | null;
  hasPaymentLink: boolean;
  paymentLinkCode?: string | null;
  isImplicitProformaEnabled?: boolean;
}): ReactElement => {
  const navigate = useNavigate();

  const { usesPalletInbound: supplierUsesPalletInboundForOrder } =
    useOrderDistributionDeliveryArea({
      fulfillmentType,
      supplierId,
      postcode: buyerPostcode,
    });

  const mutation = useOrderCommentCreate();
  const { business } = useSellarCurrentBusiness();
  const { user } = useSellarUser();
  const buildStripeDashboardUrl = useBuildStripeDashboardUrl();
  const { isProcessed } = useSellarCreditPaymentStatus({ orderId });
  const companyCreditInfoQuery = useLegalEntityCompanyCreditInfo(
    {
      RetailerId: business?.Retailer?.id ?? 0,
    },
    {
      enabled: Boolean(business?.Retailer?.id),
    },
  );

  const companyCreditInfo = companyCreditInfoQuery.data;

  const creditProcessorDeferredPaymentQuery =
    useCreditProcessorGetDeferredPayment(deferredPaymentId || "", {
      enabled: !confirmedAt && Boolean(deferredPaymentId),
    });
  const deferredPaymentStatus =
    creditProcessorDeferredPaymentQuery.data?.status;

  const [confirmOrderDialogActive, setConfirmOrderDialogActive] =
    useState<boolean>(false);
  const [createCommentActive, setCreateCommentActive] = useState(false);
  const [requestCardPaymentDialogActive, setRequestCardPaymentDialogActive] =
    useState<boolean>(false);

  const [sendOrderInvoiceActive, setSendOrderInvoiceActive] =
    useState<boolean>(false);
  const [orderPaidActive, setOrderPaidActive] = useState<boolean>(false);
  const [sendOrderInvoiceReminderActive, setSendOrderInvoiceReminderModal] =
    useState<boolean>(false);
  const [shippingLabelActive, setShippingLabelActive] = useState(false);
  const [paymentFailedDialogActive, setPaymentFailedDialogActive] =
    useState<boolean>(false);
  const [
    cardPaymentWithDepositFailedDialogActive,
    setCardPaymentWithDepositFailedDialogActive,
  ] = useState<boolean>(false);
  const [
    cardChargeRemainingBalancePaymentFailedDialogActive,
    setCardChargeRemainingBalancePaymentFailedDialogActive,
  ] = useState<boolean>(false);

  const [paymentChangeDialogActive, setPaymentChangeDialogActive] =
    useState<boolean>(false);
  const [sendPaymentLinkDialogActive, setSendPaymentLinkDialogActive] =
    useState<boolean>(false);
  const remoteFeatureFlagQuery = useRemoteFeatureFlags();
  const isOoboSellarCreditActive =
    remoteFeatureFlagQuery.isActive("sellarCreditOobo");
  const isPaymentLinkRequestEnabled = remoteFeatureFlagQuery.isActive(
    "checkoutPaymentLinkRequest",
  );

  const getShouldShowRequestProformaPayment = () => {
    if (paymentStatus === "paid") return false;
    if (paymentType === "cardPaymentWithDeposit") return false;
    if (stripeInvoiceId) return false;

    if (isPaymentLinkRequestEnabled) {
      // Supplier needs Stripe connected to issue a card payment link.
      if (!stripeAccountId) return false;
      return !hasPaymentLink;
    }

    return !paymentDue || paymentTerms === "paymentOnDelivery";
  };

  const getShouldShowPaymentReminder = () => {
    if (paymentStatus === "paid") return false;

    if (stripeInvoiceId) return true;
    if (isPaymentLinkRequestEnabled) {
      return hasPaymentLink;
    }
    return !!paymentDue && paymentTerms !== "paymentOnDelivery";
  };

  const showRequestProformaPayment = getShouldShowRequestProformaPayment();
  const showPaymentReminder = getShouldShowPaymentReminder();

  const getShouldShowPostFinalisePaymentLinkPanel = () => {
    if (!confirmedAt) return false;
    if (paymentStatus === "paid") return false;
    if (paymentType !== "manual") return false;
    if (paymentTerms !== "proforma" && paymentTerms !== "credit") return false;
    if (!isPaymentLinkRequestEnabled) return false;
    if (!stripeAccountId) return false;
    return true;
  };

  const showPostFinalisePaymentLinkPanel =
    getShouldShowPostFinalisePaymentLinkPanel();

  const orderUsesSDPalletShipment =
    !!SupplierPalletShipment ||
    (fulfillmentType === "sellarDistribution" &&
      supplierUsesPalletInboundForOrder);

  const showSellarCreditFeatures = paymentType === "sellarCredit";
  const canShowPayWithSellarCreditActionPanel =
    showSellarCreditFeatures &&
    !confirmedAt &&
    !deferredPaymentId &&
    deferredPaymentStatus !== "accepted" &&
    // This can all be removed when we remove isOoboSellarCreditActive FF, as its not disabled with an error message
    (companyCreditInfo?.userCanAccessSellarCredit ||
      user?.type === "admin" ||
      isOoboSellarCreditActive);

  const canShowSellarCreditVatActionPanel =
    showSellarCreditFeatures && Boolean(dispatchedAt);

  const canShowSellarCreditAwaitingFinalizationPanel =
    showSellarCreditFeatures && !confirmedAt && isProcessed;

  const canShowSellarCreditOrderNotPaidActionPanel = !isProcessed;

  const paymentsQuery = usePaymentList(
    {
      query: [
        {
          type: "OrderId",
          modifier: "EQUALS",
          value: orderId,
        },
      ],
      // no limit multiple payments supported
    },
    {
      enabled:
        paymentType === "checkout" || paymentType === "cardPaymentWithDeposit",
    },
  );

  const paymentList = paymentsQuery.data?.pages[0] ?? [];
  const payment = paymentsQuery.data?.pages[0]?.[0];

  // checkout payments

  const hasNoPaymentOrPaymentStatusFailed =
    !payment || payment.status === "failed" || payment.status === "pending";

  const hasCheckoutPaymentFailed =
    paymentType === "checkout" && hasNoPaymentOrPaymentStatusFailed;

  // card payment with deposit payments

  const hasCardDepositPaymentFailed =
    paymentType === "cardPaymentWithDeposit" &&
    !paymentList.some(
      (payment) => payment.type === "cardDeposit" && payment.status === "paid",
    ) &&
    !paymentList.some(
      (payment) =>
        payment.type === "cardDeposit" && payment.status === "pending",
    );

  const hasCardDepositPaymentBeenPaid =
    paymentType === "cardPaymentWithDeposit" &&
    paymentList.some(
      (payment) => payment.type === "cardDeposit" && payment.status === "paid",
    );

  const hasCardChargeRemainingBalancePaymentFailed =
    paymentType === "cardPaymentWithDeposit" &&
    paymentStatus !== "paid" &&
    hasCardDepositPaymentBeenPaid &&
    paymentList.some(
      (payment) =>
        payment.type === null &&
        (payment.status === "failed" || payment.status === "pending"),
    );

  const invoiceUrlQuery = useOrderInvoiceUrl(orderId, {
    enabled:
      !!(
        invoiceFinalizedAt ||
        paymentStatus === "open" ||
        paymentStatus === "paid"
      ) &&
      !!business?.Retailer &&
      !!stripeInvoiceId,
  });

  const orderIntegrationValueQuery = useOrderIntegrationValueList(
    {
      orderId: orderId,
    },
    {
      enabled: fulfillmentType === "ups" && !!business?.Supplier,
    },
  );

  //
  const viewStripeInvoice = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!stripeInvoiceId || !stripeAccountId) return;
    window.open(
      buildStripeDashboardUrl({
        type: "invoice",
        stripeAccountId: stripeAccountId,
        id: stripeInvoiceId,
      }),
      "_blank",
    );
  };

  const payStripeInvoice = () => {
    if (invoiceUrlQuery.data?.invoiceUrl) {
      window.open(invoiceUrlQuery.data.invoiceUrl, "_blank");
    }
  };

  const getOrderActionPaymentStatus = () => {
    if (paymentStatus === "paid") {
      return "Payment received";
    }
    if (
      stripeInvoiceId ||
      (paymentDue && paymentTerms !== "paymentOnDelivery")
    ) {
      return "Payment requested";
    }

    if (
      orderUsesSDPalletShipment &&
      ["credit", "paymentOnDelivery"].includes(paymentTerms ?? "")
    ) {
      return `Invoice to be issued on ${invoiceCreationStage}`;
    }

    return "Awaiting finalisation";
  };

  const getBuyerOrderActionTitle = () => {
    if (paymentStatus === "paid") return "Payment received";
    if (
      (stripeInvoiceId && invoiceFinalizedAt) ||
      (paymentDue && paymentTerms !== "paymentOnDelivery")
    )
      return "Payment due today";
    return !!acceptedAt
      ? `${supplierName} is working on your order`
      : "Your order has been placed";
  };

  const getBuyerOrderActionMessage = () => {
    const hasFinalisedStripeInvoice = stripeInvoiceId && invoiceFinalizedAt;
    const hasPaymentDue =
      hasFinalisedStripeInvoice ||
      (paymentDue && paymentTerms !== "paymentOnDelivery");

    if (hasPaymentDue && paymentStatus !== "paid")
      return `${supplierName} have requested payment for this order pro-forma. Pay promptly so they can finalise your order and set your delivery date.`;

    const orderNotAccepted = !acceptedAt;
    return orderNotAccepted
      ? `We will notify you when ${supplierName} accepts your order request`
      : `We'll notify you when ${supplierName} has finalised your order and set your delivery date`;
  };

  const [termsActive, setTermsActive] = useState(false);

  const termsAndConditionsEnabled = useShowTermsAndConditionsModal();

  const handleFinaliseClick = () => {
    if (termsAndConditionsEnabled) {
      setTermsActive(true);
    } else {
      setConfirmOrderDialogActive(true);
    }
  };

  const handleTermsDismiss = (accepted: boolean) => {
    setTermsActive(false);
    if (accepted) setConfirmOrderDialogActive(true);
  };

  const isLabelFulfillType =
    fulfillmentType === "ups" || fulfillmentType === "apc";
  const canShowSellarCreditShippingLabelPanel =
    showSellarCreditFeatures &&
    Boolean(confirmedAt) &&
    Boolean(orderIntegrationValueQuery.data?.pages[0]?.length) &&
    isLabelFulfillType;

  const showNonSellarCreditShippingLabelPanel =
    Boolean(orderIntegrationValueQuery.data?.pages[0]?.length) &&
    isLabelFulfillType &&
    Boolean(confirmedAt) &&
    (paymentType === "manual" ||
      paymentStatus === "paid" ||
      paymentStatus === "open");

  if (showSellarCreditFeatures) {
    return (
      <>
        <BuyerOnly>
          {canShowSellarCreditVatActionPanel && !isImplicitProformaEnabled && (
            <SellarCreditOrderVatInvoiceActionPanel orderId={orderId} />
          )}
          {canShowPayWithSellarCreditActionPanel &&
            !isImplicitProformaEnabled && (
              <>
                {isOoboSellarCreditActive ? (
                  <PayWithSellarCreditActionPanel
                    orderId={orderId}
                    orderDeferredPaymentId={deferredPaymentId}
                    initiator={initiator}
                    supplierName={supplierName}
                    twoOrder={twoOrder}
                  />
                ) : (
                  <PayWithSellarCreditActionPanelLegacy
                    orderId={orderId}
                    orderDeferredPaymentId={deferredPaymentId}
                    twoOrder={twoOrder}
                  />
                )}
              </>
            )}
        </BuyerOnly>

        <SupplierOnly>
          {orderUsesSDPalletShipment && (
            <SellarDistributionPalletActionPanel
              order={{
                dispatchedAt,
                deliveredAt,
                SupplierPalletShipment,
                paymentStatus: paymentStatus ?? null,
                paymentType,
                paymentDue,
                stripeInvoiceId: stripeInvoiceId ?? null,
                invoiceIssuedAt,
              }}
              connection={{ paymentTerms: paymentTerms ?? "proforma" }}
              onClickMarkAsPaid={() => setOrderPaidActive(true)}
            />
          )}
          {canShowSellarCreditAwaitingFinalizationPanel && (
            <ActionPanel
              className="flex flex-col gap-4"
              title="Awaiting finalisation"
              message={`Finalise this order when you're ready to set a delivery date and fulfil this order for ${buyerName}`}
            >
              <Button
                onClick={() => {
                  handleFinaliseClick();
                }}
                className="w-full md:w-min"
              >
                <Icon size={0.7} icon="CHECK_CIRCLE" />
                Finalise
              </Button>
            </ActionPanel>
          )}
          {canShowSellarCreditOrderNotPaidActionPanel &&
            !isImplicitProformaEnabled && (
              <SellarCreditOrderNotPaid buyerVenueName={buyerName} />
            )}
          {canShowSellarCreditShippingLabelPanel && (
            <ActionPanel
              title={
                fulfillmentType === "ups"
                  ? "Your shipping labels are ready!"
                  : "Your shipping labels are ready and your collection has been booked."
              }
              message={
                fulfillmentType === "ups"
                  ? "Be sure to print and apply to the package(s) associated with this order."
                  : `Be sure to print and apply to the package(s) associated with this order. Your collection has been booked for ${dayjs(dispatchPlannedAt).format("DD/MM/YYYY")}`
              }
              className="flex flex-col gap-4"
            >
              <div className={"flex flex-col gap-2 md:flex-row"}>
                <Button
                  onClick={() => setShippingLabelActive(true)}
                  className={"w-full md:w-min"}
                >
                  <Icon size={0.7} icon="DOWNLOAD" />
                  Get labels
                </Button>
                <Button
                  color={"secondary"}
                  onClick={() => navigate(COLLECTIONS)}
                  className={"w-full md:w-min"}
                >
                  <Icon size={0.7} icon="HAND_EXTENDED" />
                  VIEW COLLECTION
                </Button>
              </div>
            </ActionPanel>
          )}
          <ConfirmOrderDialog
            active={confirmOrderDialogActive}
            onDismiss={() => setConfirmOrderDialogActive(false)}
            orderId={orderId}
            invoiceCreationStage={invoiceCreationStage}
          />
          <TermsAndConditionsDialog
            active={termsActive}
            onDismiss={handleTermsDismiss}
          />
          {(fulfillmentType === "ups" || fulfillmentType === "apc") && (
            <OrderShippingLabelModal
              active={shippingLabelActive}
              onDismiss={() => setShippingLabelActive(false)}
              orderId={orderId}
              buyerName={buyerName}
              fulfillmentType={fulfillmentType}
            />
          )}
        </SupplierOnly>
      </>
    );
  }

  if (voidedAt) {
    return <></>;
  }

  const shouldBlockUnpaidRyftOrderFinalisation =
    Boolean(business?.Supplier) &&
    !confirmedAt &&
    paymentType === "cardPayment" &&
    paymentStatus !== "paid" &&
    !paidAt;

  if (shouldBlockUnpaidRyftOrderFinalisation) {
    if (isImplicitProformaEnabled) {
      return <></>;
    }

    return (
      <ActionPanel
        title="Awaiting card payment"
        message="The buyer needs to complete their payment before you can finalise this order."
      />
    );
  }

  return (
    <>
      <SupplierOnly>
        {orderUsesSDPalletShipment && (
          <SellarDistributionPalletActionPanel
            order={{
              dispatchedAt,
              deliveredAt,
              SupplierPalletShipment,
              paymentStatus: paymentStatus ?? null,
              paymentType,
              paymentDue,
              stripeInvoiceId: stripeInvoiceId ?? null,
              invoiceIssuedAt,
            }}
            connection={{ paymentTerms: paymentTerms ?? "proforma" }}
            onClickMarkAsPaid={() => setOrderPaidActive(true)}
          />
        )}
      </SupplierOnly>

      {!isImplicitProformaEnabled &&
      paymentDue &&
      paymentStatus !== "paid" &&
      dayjs().diff(dayjs(paymentDue), "days") >= 1 ? ( // && if invoice has been sent (if payment type is credit)
        <>
          <SupplierOnly>
            <ActionPanel
              className={"flex flex-col gap-4"}
              title={`Payment overdue by ${dayjs().diff(
                dayjs(paymentDue),
                "days",
              )} days`}
              message={`This order was due for payment on ${dayjs(
                paymentDue,
              ).format("DD/MM/YY")}`}
            >
              {!confirmedAt && (
                <Button
                  onClick={() => setConfirmOrderDialogActive(true)}
                  className={"w-full md:w-min"}
                >
                  <Icon size={0.7} icon={"CHECK_CIRCLE"} />
                  Finalise
                </Button>
              )}
              {confirmedAt && paymentType === "manual" && !paidAt && (
                <div className={"flex flex-col gap-2 md:flex-row"}>
                  <Button
                    onClick={() => setOrderPaidActive(true)}
                    className={"w-full md:w-min"}
                  >
                    Mark as paid
                  </Button>
                  {(!!stripeInvoiceId || paymentDue) && (
                    <Button
                      onClick={() => {
                        setSendOrderInvoiceReminderModal(true);
                      }}
                      className={"w-full md:w-min"}
                      color={"secondary"}
                    >
                      <Icon size={0.7} icon={"CREDIT_CARD_FAST_OUTLINE"} />
                      Send payment reminder
                    </Button>
                  )}
                </div>
              )}
              {(!!stripeInvoiceId || paymentDue) && !confirmedAt && (
                <Button
                  onClick={() => {
                    setSendOrderInvoiceReminderModal(true);
                  }}
                  className={"w-full md:w-min"}
                  color={"secondary"}
                >
                  <Icon size={0.7} icon={"CREDIT_CARD_FAST_OUTLINE"} />
                  Send payment reminder
                </Button>
              )}
              {confirmedAt && paymentType === "stripe" && stripeInvoiceId && (
                <div className={"flex flex-col gap-2 md:flex-row"}>
                  {!invoiceFinalizedAt && paymentStatus !== "open" && (
                    <Button
                      onClick={() => {
                        setSendOrderInvoiceActive(true);
                      }}
                      className={"w-full md:w-min"}
                    >
                      <Icon size={0.7} icon={"SEND"} />
                      Send invoice
                    </Button>
                  )}
                  {(invoiceFinalizedAt || paymentStatus === "open") && (
                    <Button
                      onClick={() => {
                        setSendOrderInvoiceReminderModal(true);
                      }}
                      className={"w-full md:w-min"}
                    >
                      <Icon size={0.7} icon={"SEND"} />
                      Send Reminder
                    </Button>
                  )}
                  <Button
                    color={"secondary"}
                    onClick={(e) => viewStripeInvoice(e)}
                    className={"w-full md:w-min"}
                  >
                    View on Stripe
                  </Button>
                </div>
              )}
            </ActionPanel>
          </SupplierOnly>
          <BuyerOnly>
            {paymentType === "stripe" && (
              <ActionPanel
                type={"error"}
                className={"flex flex-col gap-4"}
                title={`Payment overdue by ${dayjs().diff(
                  dayjs(paymentDue),
                  "days",
                )} days`}
                message={
                  paymentType === "stripe"
                    ? `This order was due for payment on ${dayjs(
                        paymentDue,
                      ).format(
                        "DD/MM/YY",
                      )}. Please pay now using credit or debit card. If you paid by other means, add a comment to this order to let ${supplierName} know.`
                    : ""
                }
              >
                <div className={"flex flex-col gap-2 md:flex-row"}>
                  {paymentType === "stripe" &&
                    stripeInvoiceId &&
                    (invoiceFinalizedAt || paymentStatus === "open") &&
                    invoiceUrlQuery.data?.invoiceUrl && (
                      <Button
                        color={"red"}
                        className={"w-full md:w-min"}
                        onClick={() => payStripeInvoice()}
                      >
                        <Icon size={0.7} icon={"CREDIT_CARD"} />
                        Pay by card
                      </Button>
                    )}
                  <Button
                    color={"inverse-red"}
                    className={"w-full md:w-min"}
                    onClick={() => setCreateCommentActive(true)}
                  >
                    Add comment
                  </Button>
                </div>
              </ActionPanel>
            )}
            {paymentType !== "checkout" &&
              paymentType !== "stripe" &&
              paymentType !== "cardPaymentWithDeposit" &&
              paymentDue &&
              paymentTerms !== "paymentOnDelivery" && (
                <OrderVatInvoiceActionPanel orderId={orderId} />
              )}

            <CommentDialog
              orderId={orderId}
              active={createCommentActive}
              onDismiss={() => setCreateCommentActive(false)}
              type={"comment"}
              mutation={mutation}
            />
          </BuyerOnly>
        </>
      ) : (
        <>
          <SupplierOnly>
            {!confirmedAt && (
              <>
                {(hasCheckoutPaymentFailed || hasCardDepositPaymentFailed) &&
                  !isImplicitProformaEnabled && (
                    <ActionPanel
                      className={"flex flex-col gap-4"}
                      title={"Payment failed at checkout"}
                      message={`We were unable to process ${buyerName}'s pro-forma card payment at checkout. ${hasCheckoutPaymentFailed ? `You can issue them a pro-forma invoice by editing their payment method.` : ""} `}
                    >
                      <>
                        {hasCardDepositPaymentFailed ? (
                          <div>
                            Inform the buyer they must retry with a different
                            card
                          </div>
                        ) : (
                          <Button
                            onClick={() => setPaymentChangeDialogActive(true)}
                          >
                            Edit payment method
                          </Button>
                        )}
                      </>
                    </ActionPanel>
                  )}
                {!hasCheckoutPaymentFailed &&
                (!hasCardDepositPaymentFailed || isImplicitProformaEnabled) ? (
                  <ActionPanel
                    className={"flex flex-col gap-4"}
                    title={
                      isImplicitProformaEnabled
                        ? "Awaiting finalisation"
                        : getOrderActionPaymentStatus()
                    }
                    message={
                      isImplicitProformaEnabled
                        ? `Finalise this order when you're ready to set a delivery date and fulfil this order for ${buyerName}`
                        : (() => {
                            const isDepositFlow =
                              paymentType === "cardPaymentWithDeposit";

                            const shouldShowFinaliseMessage =
                              (!!stripeInvoiceId ||
                                (paymentDue &&
                                  paymentTerms !== "paymentOnDelivery") ||
                                paymentStatus === "paid") &&
                              !orderUsesSDPalletShipment;

                            const canRequestProForma = !!stripeAccountId;

                            const shouldShowPalletInboundCreditTermsIssueProformaMessage =
                              paymentStatus !== "paid" &&
                              orderUsesSDPalletShipment &&
                              ["credit", "paymentOnDelivery"].includes(
                                paymentTerms ?? "",
                              ) &&
                              canRequestProForma;

                            if (!isDepositFlow) {
                              if (shouldShowFinaliseMessage) {
                                return `Finalise this order when you're ready to set a delivery date and fulfil this order for ${buyerName}`;
                              } else if (
                                shouldShowPalletInboundCreditTermsIssueProformaMessage
                              ) {
                                return `We will issue your customer's invoice for you${dueDays ? `, and they will have ${dueDays} days to pay for their order.` : "."} You can request payment sooner by issuing a pro-forma invoice.`;
                              } else if (
                                canRequestProForma &&
                                !orderUsesSDPalletShipment
                              ) {
                                return `You can request pro-forma payment for this order before the delivery date is finalised. Make any edits to the order before sending the invoice or finalising.`;
                              }
                            }

                            return "";
                          })()
                    }
                  >
                    <div className={"flex flex-col gap-2 md:flex-row"}>
                      <Button
                        onClick={handleFinaliseClick}
                        className={"w-full md:w-min"}
                      >
                        <Icon size={0.7} icon={"CHECK_CIRCLE"} />
                        Finalise
                      </Button>

                      {!isImplicitProformaEnabled &&
                        showRequestProformaPayment &&
                        (isPaymentLinkRequestEnabled ? (
                          <Button
                            onClick={() => setSendPaymentLinkDialogActive(true)}
                            className={"w-full md:w-min"}
                            color={"secondary"}
                          >
                            <Icon
                              size={0.7}
                              icon={"CREDIT_CARD_FAST_OUTLINE"}
                            />
                            Request pro-forma payment
                          </Button>
                        ) : (
                          <Button
                            onClick={() =>
                              setRequestCardPaymentDialogActive(true)
                            }
                            className={"w-full md:w-min"}
                            color={"secondary"}
                          >
                            <Icon
                              size={0.7}
                              icon={"CREDIT_CARD_FAST_OUTLINE"}
                            />
                            Request pro-forma payment
                          </Button>
                        ))}

                      {!isImplicitProformaEnabled && showPaymentReminder && (
                        <Button
                          onClick={() => {
                            const hasStripeInvoiceIssued =
                              paymentType === "stripe" &&
                              Boolean(invoiceIssuedAt);
                            if (
                              isPaymentLinkRequestEnabled &&
                              !hasStripeInvoiceIssued
                            ) {
                              setSendPaymentLinkDialogActive(true);
                            } else {
                              setSendOrderInvoiceReminderModal(true);
                            }
                          }}
                          className={"w-full md:w-min"}
                          color={"secondary"}
                        >
                          <Icon size={0.7} icon={"CREDIT_CARD_FAST_OUTLINE"} />
                          Send payment reminder
                        </Button>
                      )}
                    </div>
                  </ActionPanel>
                ) : null}
              </>
            )}
            {confirmedAt &&
              paymentStatus !== "paid" &&
              paymentType === "stripe" &&
              stripeInvoiceId &&
              !invoiceFinalizedAt &&
              paymentStatus !== "open" &&
              !isImplicitProformaEnabled && (
                <>
                  <ActionPanel
                    title={"Send invoice for card payment"}
                    message={`You can choose to issue the invoice for this order now. Otherwise it will be issued automatically on order ${invoiceCreationStage === "confirmed" ? "finalized" : invoiceCreationStage} as defined by your invoice settings.`}
                    className={"flex flex-col gap-4"}
                  >
                    <div className={"flex flex-col gap-2 md:flex-row"}>
                      <Button
                        onClick={() => setSendOrderInvoiceActive(true)}
                        className={"w-full md:w-min"}
                      >
                        <Icon size={0.7} icon={"SEND"} />
                        Send invoice
                      </Button>
                      <Button
                        color={"secondary"}
                        onClick={(e) => viewStripeInvoice(e)}
                        className={"w-full md:w-min"}
                      >
                        View on Stripe
                      </Button>
                    </div>
                  </ActionPanel>
                </>
              )}
            {showPostFinalisePaymentLinkPanel && !isImplicitProformaEnabled && (
              <ActionPanel
                className={"flex flex-col gap-4"}
                title={
                  hasPaymentLink
                    ? "Card payment link sent"
                    : "Send card payment link"
                }
                message={
                  hasPaymentLink
                    ? `Send ${buyerName} a reminder to pay using their card payment link.`
                    : `Send ${buyerName} a card payment link so they can pay by card.`
                }
              >
                {hasPaymentLink ? (
                  <Button
                    onClick={() => setSendPaymentLinkDialogActive(true)}
                    className={"w-full md:w-min"}
                    color={"secondary"}
                  >
                    <Icon size={0.7} icon={"CREDIT_CARD_FAST_OUTLINE"} />
                    Send payment reminder
                  </Button>
                ) : (
                  <Button
                    onClick={() => setSendPaymentLinkDialogActive(true)}
                    className={"w-full md:w-min"}
                  >
                    <Icon size={0.7} icon={"CREDIT_CARD_FAST_OUTLINE"} />
                    Send card payment link
                  </Button>
                )}
              </ActionPanel>
            )}
            {showNonSellarCreditShippingLabelPanel && (
              <>
                <ActionPanel
                  title={
                    fulfillmentType === "ups"
                      ? "Your shipping labels are ready!"
                      : "Your shipping labels are ready and your collection has been booked."
                  }
                  message={
                    fulfillmentType === "ups"
                      ? "Be sure to print and apply to the package(s) associated with this order."
                      : `Be sure to print and apply to the package(s) associated with this order. Your collection has been booked for ${dayjs(dispatchPlannedAt).format("DD/MM/YYYY")}`
                  }
                  className="flex flex-col gap-4"
                >
                  <div className={"flex flex-col gap-2 md:flex-row"}>
                    <Button
                      onClick={() => setShippingLabelActive(true)}
                      className={"w-full md:w-min"}
                    >
                      <Icon size={0.7} icon="DOWNLOAD" />
                      Get labels
                    </Button>
                    <Button
                      color={"secondary"}
                      onClick={() => navigate(COLLECTIONS)}
                      className={"w-full md:w-min"}
                    >
                      <Icon size={0.7} icon="HAND_EXTENDED" />
                      VIEW COLLECTION
                    </Button>
                  </div>
                </ActionPanel>
              </>
            )}
            <TermsAndConditionsDialog
              active={termsActive}
              onDismiss={handleTermsDismiss}
            />
          </SupplierOnly>

          <BuyerOnly>
            {!confirmedAt && !isImplicitProformaEnabled && (
              <>
                {hasCheckoutPaymentFailed || hasCardDepositPaymentFailed ? (
                  <ActionPanel
                    className={"flex flex-col gap-4"}
                    title={"Payment failed"}
                    message={
                      "We were unable to process your payment. Use the button below to retry your card payment."
                    }
                  >
                    <Button
                      onClick={() =>
                        hasCardDepositPaymentFailed
                          ? setCardPaymentWithDepositFailedDialogActive(true)
                          : setPaymentFailedDialogActive(true)
                      }
                    >
                      {" "}
                      <Icon icon={"CREDIT_CARD"} />
                      Make payment
                    </Button>
                  </ActionPanel>
                ) : (
                  <>
                    {paymentType === "checkout" && paidAmount > price ? (
                      <ActionPanel title={"Your order has been edited"}>
                        <p>
                          {supplierName} has made changes to this order
                          resulting in a lower order total than what you
                          originally paid. When this order is finalised, a
                          difference of {formatCurrency(paidAmount - price)}{" "}
                          will be <strong>automatically refunded</strong> to
                          your payment card.
                        </p>
                      </ActionPanel>
                    ) : (
                      <ActionPanel
                        className={"flex flex-col gap-4"}
                        title={getBuyerOrderActionTitle()}
                        message={getBuyerOrderActionMessage()}
                      >
                        {paymentType === "stripe" &&
                          paymentStatus !== "paid" && (
                            <TooltipContainer
                              enable={
                                !stripeInvoiceId ||
                                !invoiceFinalizedAt ||
                                paymentStatus !== "open"
                              }
                              placement="top"
                              text={`${supplierName} haven’t sent the invoice yet`}
                            >
                              <Button
                                disabled={
                                  !stripeInvoiceId ||
                                  !invoiceFinalizedAt ||
                                  paymentStatus !== "open"
                                }
                                onClick={() => payStripeInvoice()}
                                className={"w-full md:w-min"}
                              >
                                <Icon size={0.7} icon={"CREDIT_CARD"} />
                                Pay by card
                              </Button>
                            </TooltipContainer>
                          )}
                        {hasPaymentLink && !paidAt && (
                          <Button
                            onClick={() => {
                              if (!paymentLinkCode) return;
                              navigate(
                                PAYMENT_LINK_REF.href({
                                  code: paymentLinkCode,
                                }),
                              );
                            }}
                            className={"w-full md:w-min"}
                            color={"secondary"}
                          >
                            <Icon size={0.7} icon={"CREDIT_CARD"} />
                            View payment link
                          </Button>
                        )}
                      </ActionPanel>
                    )}
                  </>
                )}
              </>
            )}
            {confirmedAt &&
              paymentStatus !== "paid" &&
              !isImplicitProformaEnabled &&
              (paymentType === "stripe" ||
                (paymentType === "manual" && stripeInvoiceId)) && (
                <ActionPanel
                  className={"flex flex-col gap-4"}
                  title={
                    paymentDue
                      ? `Payment due ${
                          dayjs().isSame(dayjs(paymentDue), "day")
                            ? "today"
                            : dayjs(paymentDue).diff(dayjs(), "days") < 1
                              ? "tomorrow"
                              : `in ${
                                  dayjs(paymentDue).diff(dayjs(), "days") + 1
                                } days`
                        }`
                      : "Payment due today"
                  }
                  message={
                    paymentDue
                      ? `You can pay using credit or debit card on or before ${dayjs(paymentDue).format("DD/MM/YY")}. ${supplierName} will issue your invoice on ${invoiceCreationStage === "confirmed" ? "finalized" : invoiceCreationStage}}`
                      : "You can pay using credit or debit card."
                  }
                >
                  <TooltipContainer
                    enable={
                      (!invoiceFinalizedAt && paymentStatus !== "open") ||
                      !invoiceUrlQuery.data?.invoiceUrl
                    }
                    placement="top"
                    text={`${supplierName} haven’t sent the invoice yet`}
                  >
                    <Button
                      disabled={
                        (!invoiceFinalizedAt && paymentStatus !== "open") ||
                        !invoiceUrlQuery.data?.invoiceUrl
                      }
                      onClick={() => payStripeInvoice()}
                      className={"w-full md:w-min"}
                    >
                      <Icon size={0.7} icon={"CREDIT_CARD"} />
                      Pay by card
                    </Button>
                  </TooltipContainer>
                </ActionPanel>
              )}
            {((paymentType !== "checkout" &&
              paymentType !== "stripe" &&
              paymentType !== "cardPaymentWithDeposit" &&
              !stripeInvoiceId &&
              paymentDue &&
              paymentTerms !== "paymentOnDelivery" &&
              ((invoiceCreationStage === "dispatched" && dispatchedAt) ||
                (invoiceCreationStage === "delivered" && deliveredAt) ||
                invoiceCreationStage === "confirmed")) ||
              (!confirmedAt &&
                paymentType !== "stripe" &&
                paymentType !== "checkout" &&
                paymentType !== "cardPaymentWithDeposit" &&
                invoiceIssuedAt) ||
              (paymentType === "manual" &&
                !paidAt &&
                hasPaymentLink &&
                !stripeInvoiceId &&
                paymentDue &&
                paymentTerms !== "paymentOnDelivery")) &&
              !isImplicitProformaEnabled && (
                <OrderVatInvoiceActionPanel orderId={orderId} />
              )}
          </BuyerOnly>
        </>
      )}
      <SupplierOnly>
        <ConfirmOrderDialog
          active={confirmOrderDialogActive}
          onDismiss={() => setConfirmOrderDialogActive(false)}
          orderId={orderId}
          invoiceCreationStage={invoiceCreationStage}
        />
        <OrderSendInvoiceModal
          onSuccess={() => setMessageType?.("invoiceSent")}
          active={sendOrderInvoiceActive}
          onDismiss={() => setSendOrderInvoiceActive(false)}
          orderId={orderId}
          buyerName={buyerName}
          paymentDue={paymentDue}
        />
        <OrderSendInvoiceReminderModal
          active={sendOrderInvoiceReminderActive}
          onDismiss={() => setSendOrderInvoiceReminderModal(false)}
          orderId={orderId}
          buyerName={buyerName}
          onSuccess={() => setMessageType?.("reminderSent")}
          paymentType={paymentType}
        />

        <OrderPaidModal
          active={orderPaidActive}
          onDismiss={() => setOrderPaidActive(false)}
          orderId={orderId}
          paymentType={paymentType}
        />
        {paymentType !== "checkout" &&
          paymentType !== "cardPaymentWithDeposit" && (
            <OrderRequestCardPaymentModal
              onDismiss={() => setRequestCardPaymentDialogActive(false)}
              active={requestCardPaymentDialogActive}
              orderId={orderId}
              buyerName={buyerName}
              onSuccess={() => setMessageType?.("invoiceSent")}
              stripeAccountId={stripeAccountId}
              paymentType={paymentType}
              hasNoDraughtDutyRelief={hasNoDraughtDutyRelief}
            />
          )}
        <SendPaymentLinkModal
          active={sendPaymentLinkDialogActive}
          onDismiss={() => setSendPaymentLinkDialogActive(false)}
          key={orderId}
          orderId={orderId}
          retailerInvoiceEmails={retailerInvoiceEmails ?? []}
          hasBankDetails={hasBankDetails}
          invoicesEnabled={invoicesEnabled}
          invoiceCreationStage={invoiceCreationStage}
        />
        {(fulfillmentType === "ups" || fulfillmentType === "apc") && (
          <OrderShippingLabelModal
            active={shippingLabelActive}
            onDismiss={() => setShippingLabelActive(false)}
            orderId={orderId}
            buyerName={buyerName}
            fulfillmentType={fulfillmentType}
          />
        )}
      </SupplierOnly>
      <BuyerOnly>
        {hasCardDepositPaymentFailed &&
          !!confirmedAt &&
          !isImplicitProformaEnabled && (
            <ActionPanel
              className={"flex flex-col gap-4"}
              title={"Payment failed"}
              message={
                "We were unable to process your deposit payment. Use the button below to retry your card payment."
              }
            >
              <Button
                onClick={() => {
                  setCardPaymentWithDepositFailedDialogActive(true);
                }}
              >
                {" "}
                <Icon icon={"CREDIT_CARD"} />
                Make payment
              </Button>
            </ActionPanel>
          )}
        {hasCardChargeRemainingBalancePaymentFailed &&
          !isImplicitProformaEnabled && (
            <ActionPanel
              className={"flex flex-col gap-4"}
              title={"Remaining balance payment failed"}
              message={
                "We were unable to process your final balance payment. Use the button below to retry your card payment."
              }
            >
              <Button
                onClick={() => {
                  setCardChargeRemainingBalancePaymentFailedDialogActive(true);
                }}
              >
                {" "}
                <Icon icon={"CREDIT_CARD"} />
                Make payment
              </Button>
            </ActionPanel>
          )}
      </BuyerOnly>
      <PaymentFailedDialog
        orderId={orderId}
        active={paymentFailedDialogActive}
        onDismiss={() => setPaymentFailedDialogActive(false)}
      />
      <PaymentChangeDialog
        orderId={orderId}
        active={paymentChangeDialogActive}
        onDismiss={() => setPaymentChangeDialogActive(false)}
        stripeAccountId={stripeAccountId}
      />

      <CardPaymentWithDepositFailedDialog
        orderId={orderId}
        active={cardPaymentWithDepositFailedDialogActive}
        onDismiss={() => setCardPaymentWithDepositFailedDialogActive(false)}
      />

      <CardChargeRemainingBalancePaymentFailedDialog
        orderId={orderId}
        active={cardChargeRemainingBalancePaymentFailedDialogActive}
        onDismiss={() =>
          setCardChargeRemainingBalancePaymentFailedDialogActive(false)
        }
      />
    </>
  );
};

export const PaymentChangeDialog = ({
  active,
  onDismiss,
  orderId,
  stripeAccountId,
}: {
  active: boolean;
  onDismiss: () => void;
  orderId: number;
  stripeAccountId?: string | null;
}) => {
  const { isActive } = useRemoteFeatureFlags();
  const isPaymentLinkRequestActive = isActive("checkoutPaymentLinkRequest");

  const [newPaymentType, setNewPaymentType] =
    useState<OrderPaymentType>("stripe");
  const [paymentLinkRequested, setPaymentLinkRequested] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [error, setError] = useState<string>();

  const orderUpdateMutation = useOrderUpdate(orderId);

  const onSubmit = () => {
    setError(undefined);

    orderUpdateMutation.mutate(
      {
        requestingBusinessType: "supplier",
        paymentType: isPaymentLinkRequestActive ? "manual" : newPaymentType,
        ...(isPaymentLinkRequestActive &&
        paymentLinkRequested &&
        stripeAccountId
          ? { paymentLinkRequested: true }
          : {}),
      },
      {
        onSuccess: () => {
          onDismiss();
        },
        onError: (error) => {
          const errorMessage = apiV2AssertError(error)?.response?.data.message;
          setError(errorMessage ?? "An unknown error has occurred");
        },
      },
    );
  };

  return (
    <>
      <Dialog active={active} onDismiss={onDismiss}>
        <DialogNavigation
          leftIcon={"CHEVRON_DOWN"}
          onClickLeftIcon={onDismiss}
          title={"Edit payment method"}
          description={
            isPaymentLinkRequestActive
              ? "Change the payment method for this order."
              : "Have your customer retry their payment by issuing them a card payment link, or a BACS invoice."
          }
        />
        <DialogContent className={"flex flex-col gap-4"}>
          {error && (
            <Alert
              type="error"
              title="An error occured"
              message={error}
            ></Alert>
          )}
          {isPaymentLinkRequestActive ? (
            <>
              <InputLabel>Payment method</InputLabel>
              <RadioSelector value={"manual"} required={true}>
                <RadioSelectorOption value={"manual"}>
                  <H5>Direct customer payment</H5>
                </RadioSelectorOption>
              </RadioSelector>
              {stripeAccountId && (
                <div className="flex items-center gap-4 rounded-lg bg-zinc-100 p-4">
                  <Switch
                    checked={paymentLinkRequested}
                    onChange={setPaymentLinkRequested}
                  />
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-center gap-2">
                      <span className="text-base leading-6 font-semibold text-zinc-800">
                        Send a card payment link
                      </span>
                    </div>
                    <p className="text-base leading-6 font-normal text-zinc-600">
                      A link to pay by card will be sent to your customer's
                      email address when payment for this order is due.{" "}
                      <span
                        onClick={() => setInfoModalOpen(true)}
                        className={
                          "inline-flex cursor-pointer items-center gap-1 align-middle text-blue-700"
                        }
                      >
                        Learn more
                        <Icon icon={"ARROW_TOP_RIGHT"} />
                      </span>
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <InputLabel>Payment method</InputLabel>
              <RadioSelector
                onChange={(e) =>
                  setNewPaymentType(e.currentTarget.value as OrderPaymentType)
                }
                required={true}
                value={newPaymentType}
              >
                <RadioSelectorOption value={"stripe"}>
                  <H5>Card payment link</H5>
                  <p>
                    Your customer will receive an invoice issued via Stripe.
                  </p>
                </RadioSelectorOption>
                <RadioSelectorOption value={"manual"}>
                  <H5>BACS invoice</H5>
                  <p>
                    Arrange payment outside of Sellar. Your customer will be
                    able to access their invoice from the Sellar app.
                  </p>
                </RadioSelectorOption>
              </RadioSelector>
            </>
          )}
          <Alert
            type="information"
            title="The current payment intent will be cancelled in Stripe"
            message="This means you will be responsible for collecting payment via the method you have selected above"
          ></Alert>
        </DialogContent>
        <DialogCTA className={"flex flex-col gap-2 md:flex-row"}>
          <Button
            loading={orderUpdateMutation.isPending}
            onClick={onSubmit}
            className={"w-full md:w-auto"}
          >
            Confirm
          </Button>
        </DialogCTA>
      </Dialog>

      {isPaymentLinkRequestActive && (
        <Dialog
          active={infoModalOpen}
          onDismiss={() => setInfoModalOpen(false)}
        >
          <DialogNavigation
            onClickLeftIcon={() => setInfoModalOpen(false)}
            leftIcon={"CHEVRON_DOWN"}
            title={"About card payment links"}
          />
          <DialogContent className={"px-4 pb-4"}>
            <div className="flex flex-col gap-3 text-base text-zinc-700">
              <p>
                By toggling on{" "}
                <span className="font-semibold">
                  &lsquo;Send a card payment link&rsquo;
                </span>
                , your customer will be sent a link to their email address when
                payment for their order is due.
              </p>
              <ul className="list-disc pl-5">
                <li>
                  Your customer can access the link any time from their view of
                  the order on Sellar.
                </li>
                <li>
                  Payment is made directly to your connected Stripe account.
                </li>
              </ul>
              <Alert
                type={"information"}
                title={"You are still responsible for invoicing the customer"}
                message={
                  <>
                    The payment link simply allows you to collect payment
                    through Sellar.{" "}
                    <span className="font-semibold">
                      If you don&rsquo;t include a card payment link
                    </span>
                    , you will be responsible for collecting payment from your
                    customer outside of Sellar.
                  </>
                }
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
